import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
  BbitCalendarDisplayStyle,
  BbitCalendarEventType,
  BbitCalendarType,
  BbitI18n,
  BbitTime,
  IBbitCalendar,
  IBbitCalendarEvent,
  IBbitCalendarNewEventOptions,
  IBbitCalendarOptions,
  IBbitI18n,
} from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService, I18nPipe, LuxonPipe } from '@bbitgmbh/website.sdk';
import { DateTime, Duration } from 'luxon';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { IBbitBookingOption, PlaceBookingService } from '../booking.service';
import { IBbitBookingOverlayData } from '../booking-overlay/booking-overlay.ref';
import { BookingOverlayService } from '../booking-overlay.service';
import { CalendarComponent } from '../calendar/calendar.component';

export interface IBbitPlaceBookingBookingsRequest {
  places: string[];
  date: string;
}

export interface IBbitPlaceBookingBookingsResponse {
  date: string;
  isClosed: boolean;
  isBlocked?: boolean;
  success: boolean;
  places: IBbitPlaceBookingPlace[];
  events: IBbitPlaceBookingEvent[];
  openingTime?: string;
  closingTime?: string;
}

export interface IBbitPlaceBookingPlace {
  _id: string;
  designation: IBbitI18n;
}

export interface IBbitPlaceBookingEvent {
  _id: string;
  date: string;
  from: string;
  until: string;
  place: string;
  color?: string;
  designation: IBbitI18n;
  isOwnBooking: boolean;
  isClosed?: boolean;
  isBlocked?: boolean;
  lastPossibleCancellationDate?: string;
}

export interface IBbitPlaceBookingBookingBookRequest {
  place: string;
  date: string;
  from: string;
  until: string;
  usageType?: string;
}

export interface IBbitPlaceBookingBookResponse {
  success: boolean;
}

@Component({
  selector: 'app-booking',
  templateUrl: './booking.component.html',
  styleUrl: './booking.component.scss',
  imports: [CalendarComponent, AsyncPipe, LuxonPipe, I18nPipe],
})
export class BookingComponent {
  public calendarOptions = new BehaviorSubject<IBbitCalendarOptions | undefined>(undefined);
  private _eventSubject = new BehaviorSubject<IBbitCalendarEvent[]>([]);
  private _calendarSubject = new BehaviorSubject<IBbitCalendar[]>([]);
  private _option!: IBbitBookingOption;
  private _date = new BehaviorSubject<DateTime>(DateTime.local({ zone: 'Europe/Zurich' }).startOf('day'));
  public displayDate = new BehaviorSubject<DateTime>(DateTime.local({ zone: 'Europe/Zurich' }).startOf('day'));
  private _displayHours = new BehaviorSubject({ from: 8, to: 18 });
  public state = new BehaviorSubject<'closed' | 'blocked' | 'open'>('open');
  private _closingTime: DateTime | undefined;
  private _overlayService = inject(BookingOverlayService);

  constructor(
    private _overlay: BookingOverlayService,
    private _website: BbitWebsiteService,
    activatedRoute: ActivatedRoute,
    private _service: PlaceBookingService,
  ) {
    activatedRoute.queryParams.pipe(takeUntilDestroyed()).subscribe((params) => {
      if (params['option']) {
        this._option = this._service.getBookingOption(params['option']) as IBbitBookingOption;
        if (this._option) {
          this.initialize();
        } else {
          // TODO redirect to overview
        }
      }
    });

    this._date.pipe(takeUntilDestroyed()).subscribe(() => {
      this.reload();
    });
  }

  public async initialize() {
    const newEventOptions: IBbitCalendarNewEventOptions = {
      designation: { key: 'newBooking', n: 1 },
      startOffset: this._option?.startOffset || Duration.fromObject({ minutes: 0 }),
      snapGrid: this._option?.snapGrid || Duration.fromObject({ minutes: 30 }),
      duration: this._option?.duration || Duration.fromObject({ minutes: 30 }),
      preventOverlapping: true,
      preventInPast: true,
      preventInPastDesignation: { key: 'reservationNoLongerPossible', n: 1 },
    };

    this.calendarOptions.next({
      displayStyle: BbitCalendarDisplayStyle.BOOKING,
      date: this._date,
      onDateChanged: async (date: DateTime) => {
        this._date.next(date);
      },
      calendars: this._calendarSubject,
      events: this._eventSubject,
      displayHours: this._displayHours,
      newEventOptions: new BehaviorSubject(newEventOptions),
      onNewEvent: async (event: IBbitCalendarEvent): Promise<void> => {
        if (!this._website.getProfile()?.placeBooking?.isAllowedToBook) {
          this._overlayService.openInfoOverlay();
          return;
        }
        this._openBookingOverlay(event);
      },
    });
  }

  public async reload() {
    if (!this._option) {
      return;
    }

    const calendars: IBbitCalendar[] = [];

    const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/bookings`, {
      method: 'POST',
      headers: {
        Authorization: `${this._website.getAccessToken()}`,
      },
      body: JSON.stringify({
        date: this._date.value.toISO(),
        places: this._option.places,
      }),
    });

    const result: IBbitPlaceBookingBookingsResponse = await response.json();

    this.displayDate.next(DateTime.fromISO(result.date, { zone: 'Europe/Zurich' }));

    if (result.isBlocked) {
      this.state.next('blocked');
      this._displayHours.next({ from: 0, to: 0 });
    } else if (result.isClosed) {
      this.state.next('closed');
      this._displayHours.next({ from: 0, to: 0 });
    } else {
      this.state.next('open');
      const openingTime = DateTime.fromISO(result.openingTime || '', { zone: 'Europe/Zurich' });
      const openingTimeDecimal = openingTime.hour + openingTime.minute / 60;
      this._closingTime = DateTime.fromISO(result.closingTime || '', { zone: 'Europe/Zurich' });
      const closingTimeDecimal = this._closingTime.hour + this._closingTime.minute / 60;
      const roundedOpening = Math.floor(openingTimeDecimal);
      const roundedClosing = Math.min(24, Math.ceil(closingTimeDecimal));
      this._displayHours.next({ from: roundedOpening, to: roundedClosing });
    }

    for (const calendar of result.places || []) {
      calendars.push({
        _id: calendar._id,
        type: BbitCalendarType.ROOM,
        designation: BbitI18n.t(calendar.designation),
        timezone: 'Europe/Zurich',
      });
    }
    this._calendarSubject.next(calendars);

    const events: IBbitCalendarEvent[] = [];
    for (const event of result.events || []) {
      const calendar = calendars.find((o) => o._id === event.place);
      let color = '#9c0000';
      if (event.isBlocked) {
        color = '#F0F0F0';
      } else if (event.isClosed) {
        color = '#C5C5C5';
      } else if (event.isOwnBooking) {
        color = '#058700';
      }
      if (calendar) {
        events.push({
          _id: event._id,
          calendar,
          from: BbitTime.fromISOTime(event.from, { zone: 'Europe/Zurich' }).setToDateTime(this._date.value),
          until: event.until?.startsWith('T00:00:00.000')
            ? this._date.value.plus({ days: 1 }).startOf('day')
            : BbitTime.fromISOTime(event.until, { zone: 'Europe/Zurich' }).setToDateTime(this._date.value),
          type: event.isClosed ? BbitCalendarEventType.OPENING_HOURS : BbitCalendarEventType.DEFAULT,
          designation: BbitI18n.t(event.designation),
          color,
          timezone: 'Europe/Zurich',
        });
      }
    }
    this._eventSubject.next(events);
  }

  private async _openBookingOverlay(event: IBbitCalendarEvent): Promise<void> {
    const params: IBbitBookingOverlayData = {
      event,
      option: this._option,
      events: this._eventSubject.value,
      closingTime: this._closingTime || this._date.value.endOf('day'),
    };
    const reloadSubject = new Subject<boolean>();
    reloadSubject.pipe(take(1)).subscribe((reload) => {
      if (reload) {
        this.reload();
      }
    });
    await this._overlay.openBookingOverlay(params, reloadSubject);
  }
}
